/**
 * Audio bus settings (AD-861, docs/architecture/client-audio.md §3): master, music, sfx and voice,
 * each with a volume and a mute, persisted in localStorage. Pure: no Web Audio, no DOM beyond the
 * injected Storage.
 *
 * Default is SOUND ON (design §3.1): the platform already holds every sound until the first tap,
 * the iOS audio session is set to `ambient` (the ringer switch silences it and the player's own
 * music keeps playing), and a mute is one tap in Settings and persists.
 */

export type AudioBus = 'master' | 'music' | 'sfx' | 'voice';
export const AUDIO_BUSES: readonly AudioBus[] = ['master', 'music', 'sfx', 'voice'];

export interface BusSetting {
  /** 0..1, linear gain. */
  volume: number;
  muted: boolean;
}

export type AudioSettings = Record<AudioBus, BusSetting>;

export const AUDIO_SETTINGS_KEY = 'gc.audio.v1';

export const DEFAULT_AUDIO_SETTINGS: Readonly<AudioSettings> = Object.freeze({
  master: { volume: 0.8, muted: false },
  music: { volume: 0.5, muted: false },
  sfx: { volume: 0.8, muted: false },
  voice: { volume: 1, muted: false },
});

const clamp01 = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback;

/** Parse stored settings; anything malformed falls back per field to the default. */
export function parseAudioSettings(raw: string | null): AudioSettings {
  let json: unknown = null;
  try {
    json = raw === null ? null : JSON.parse(raw);
  } catch {
    json = null;
  }
  const src = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
  const out = {} as AudioSettings;
  for (const bus of AUDIO_BUSES) {
    const d = DEFAULT_AUDIO_SETTINGS[bus];
    const s = (src[bus] && typeof src[bus] === 'object' ? src[bus] : {}) as Record<string, unknown>;
    out[bus] = {
      volume: clamp01(s.volume, d.volume),
      muted: typeof s.muted === 'boolean' ? s.muted : d.muted,
    };
  }
  return out;
}

export function loadAudioSettings(storage: Pick<Storage, 'getItem'> | null): AudioSettings {
  try {
    return parseAudioSettings(storage?.getItem(AUDIO_SETTINGS_KEY) ?? null);
  } catch {
    return parseAudioSettings(null); // storage blocked (private mode): defaults, in memory
  }
}

export function saveAudioSettings(
  storage: Pick<Storage, 'setItem'> | null,
  settings: AudioSettings,
): void {
  try {
    storage?.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* quota / private mode: the setting still holds for this session */
  }
}

/** The gain a bus node should carry (its own volume, 0 when muted). Master is applied separately. */
export function busGain(settings: AudioSettings, bus: AudioBus): number {
  const s = settings[bus];
  return s.muted ? 0 : s.volume;
}

/** The effective gain a sound on `bus` reaches the speaker with (master × bus). */
export function effectiveGain(settings: AudioSettings, bus: Exclude<AudioBus, 'master'>): number {
  return busGain(settings, 'master') * busGain(settings, bus);
}
